import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import type { IImageViewPortDetails, IOrientationPreset } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { ViewsService } from '@/engine/app/Views.service';
import { useEffect, useState, type FC } from 'react';

import { TYPES } from '@/engine/ioc';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Stack } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { OrientationPresetTableRow } from './OrientationPresetTableRow';
import type { ViewController } from '@/engine/app/View.controller';
import type { IViewPortOrientation } from '@/engine/app/interfaces';
import { ViewPortKind } from '@/engine/app/interfaces';
import { SliceType } from '@/engine/app/modules/images/interfaces';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import { AddCurrentOrientationTableRow, CurrentOrientationTableRow } from './CurrentOrientationTableRow';
import type { OrientationPresetsController } from './OrientationPresets.controller';

export const OrientationPresets: FC = observer(() => {
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const viewController = useInjection<ViewController>(TYPES.ViewController);
  const controller = useInjection<OrientationPresetsController>(TYPES.OrientationPresetsController);

  const [vpImgOrientation, setVpImgOrientation] = useState<IViewPortOrientation | undefined>();

  useEffect(() => {
    let subscription: IDisposable | undefined = undefined;

    const coronalViewPortDescriptor = viewsService.currentView?.getViewPorts().find((vp) => {
      if (vp.kind !== ViewPortKind.ImageSlice) {
        return false;
      }
      const details = vp.details as IImageViewPortDetails;
      return details.sliceDescriptor === SliceType.CORONAL;
    })?.descriptor;

    if (coronalViewPortDescriptor) {
      subscription = viewController
        .getViewPortController(coronalViewPortDescriptor)
        ?.getCameraController()
        .onCameraModified((orientation) => {
          setVpImgOrientation({
            topVector: orientation.getCameraTop(),
            directionVector: orientation.getCameraDirection()
          });
        });
    } else {
      setVpImgOrientation(undefined);
    }

    return (): void => {
      subscription?.dispose();
      subscription = undefined;
    };
  }, [viewsService, viewController]);

  const currentView = viewsService.currentView;
  if (!currentView) return <ErrorIcon />;

  const orientationPresets = currentView.getOrientationPresets();

  if (orientationPresets.length === 0) {
    return null;
  }

  const vp3DOrientation = currentView.get3DViewPortDetails()?.orientation;
  if (!vp3DOrientation) {
    return <ErrorIcon />;
  }

  if (!vpImgOrientation) {
    return <ErrorIcon />;
  }

  return (
    <Container data-testid="fluoroscopyAngles">
      <BoxHeader>Fluoroscopy Angles</BoxHeader>
      <TableContainer component={Paper}>
        <Table aria-label="a dense table" size="small">
          <TableBody>
            {orientationPresets.map((preset: IOrientationPreset, index) => {
              return (
                <OrientationPresetTableRow
                  controller={controller}
                  key={preset.descriptor}
                  preset={preset}
                  index={index}
                  vp3DOrientation={vp3DOrientation}
                  vpImgOrientation={vpImgOrientation}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ '&': { marginTop: 2 } }}>
        <TableContainer component={Paper}>
          <Table aria-label="a dense table" size="small">
            <TableBody>
              <CurrentOrientationTableRow
                controller={controller}
                label="Current 2D"
                vpOrientation={vpImgOrientation}
                anotherOrientation={vp3DOrientation}
                isImagesVp={true}
              />
              <CurrentOrientationTableRow
                controller={controller}
                label="Current 3D"
                vpOrientation={vp3DOrientation}
                anotherOrientation={vpImgOrientation}
                isImagesVp={false}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <Table aria-label="a dense table" size="small" sx={{ width: '6em' }}>
          <TableBody>
            <AddCurrentOrientationTableRow controller={controller} vpOrientation={vpImgOrientation} isImagesVp={true} />
            <AddCurrentOrientationTableRow controller={controller} vpOrientation={vp3DOrientation} isImagesVp={false} />
          </TableBody>
        </Table>
      </Stack>
    </Container>
  );
});

const Container = styled.div``;
const BoxHeader = styled.h3`
  margin: 1.3em 0 0.7em 0.5em;
  font-size: 1.1em;
  font-weight: normal;
  color: ${({ theme }): string => theme.palette.secondary.light};
`;
